/**
 * Verschieben und Rueckgaengig auf dem Geraet (ADR 0013, ADR 0014).
 *
 * Die Regeln stehen in `@thw/core-domain` (`verschiebenNach`, `rueckgaengig`,
 * `zielVerboten`). Hier werden sie auf die lokale Kopie angewandt, und die
 * Buchungen gehen -- wie jede -- durch die Warteschlange (CLAUDE.md §3.3).
 */

import type { Artikel, BuchungAnlegen, Lagerort, Zustand } from "@thw/contracts";
import {
  RUECKGAENGIG_KOMMENTAR,
  rueckgaengig,
  statusRegeln,
  uebergeordneterOrt,
  verschiebenNach,
  zielVerboten,
  zustandOhneBuchung,
  type Bewegung,
  type Lage,
  type Ziel,
} from "@thw/core-domain";
import { geraeteKennung, sitzungLesen } from "../api/klient.js";
import { buchen, neueBuchungsKennung } from "../api/warteschlange.js";
import { bestandLaden, type Bestand } from "./bestand.js";
import { packstueckStandort } from "./packstueck.js";

/** Die Lage eines Artikels, wie die Fachlogik sie abfragt. */
function lageIn(bestand: Bestand): (artikel_id: string) => Lage | undefined {
  return (artikel_id) => bestand.zustaende.get(artikel_id);
}

/** „Ohne festen Platz“ -- oder `null`, solange die lokale Kopie es nicht kennt. */
export function standardziel(bestand: Bestand): Lagerort | null {
  for (const l of bestand.lagerorte.values()) {
    // Eine lokale Kopie von vor Vertrag 2.5 kennt das Feld nicht (undefined).
    if (l.standardziel && l.aktiv) return l;
  }
  return null;
}

/** Der Lagerort, der dieser Artikel ist -- bei Packstuecken. */
export function ortDesArtikels(bestand: Bestand, artikel_id: string): Lagerort | null {
  for (const l of bestand.lagerorte.values()) {
    if (l.artikel_id === artikel_id) return l;
  }
  return null;
}

/** Wo ein Lagerort gerade steckt -- Packstuecke dort, wo sie gebucht sind. */
export function uebergeordnet(bestand: Bestand, lagerort: Lagerort): Lagerort | null {
  const id = uebergeordneterOrt(lagerort, lageIn(bestand));
  return id === null ? null : (bestand.lagerorte.get(id) ?? null);
}

/** Die Unterorte eines Lagerorts, wie sie gerade stecken. Stillgelegte fehlen. */
export function unterorte(bestand: Bestand, lagerort_id: string | null): Lagerort[] {
  const lage = lageIn(bestand);
  return [...bestand.lagerorte.values()]
    .filter((l) => l.aktiv && uebergeordneterOrt(l, lage) === lagerort_id)
    .sort((a, b) => a.kennung.localeCompare(b.kennung, "de"));
}

/** Ein Packstueck nicht in sich selbst oder sein Unter-Packstueck (ADR 0013 §4). */
export function verboten(bestand: Bestand, artikel_id: string, ziel: Ziel): boolean {
  return zielVerboten(artikel_id, ziel, bestand.lagerorte, lageIn(bestand));
}

/** Ist der Artikel schon dort? Dann gibt es nichts zu buchen. */
export function schonDort(zustand: Zustand | undefined, ziel: Ziel): boolean {
  if (zustand === undefined) return false;
  if (ziel.art === "einsatzstelle") {
    return (
      zustand.verbleib === "im_einsatz" &&
      zustand.einsatzstelle_id === ziel.id &&
      zustand.lagerort_id === null
    );
  }
  return zustand.verbleib !== "nicht_erfasst" && zustand.lagerort_id === ziel.id;
}

/** Ein Ziel als kurzer Name: „T7-F1“, „Pumpenstelle Nord“. */
export function zielName(bestand: Bestand, ziel: Ziel): string {
  if (ziel.art === "einsatzstelle") {
    return bestand.einsatzstellen.get(ziel.id)?.bezeichnung ?? "unbekannte Einsatzstelle";
  }
  const l = bestand.lagerorte.get(ziel.id);
  if (l === undefined) return "unbekannter Lagerort";
  return l.standardziel || l.unbekannt ? l.bezeichnung : l.kennung;
}

/** Gibt es das Ziel noch, und ist es waehlbar? */
export function zielGueltig(bestand: Bestand, ziel: Ziel): boolean {
  if (ziel.art === "einsatzstelle") return bestand.einsatzstellen.get(ziel.id)?.status === "aktiv";
  return bestand.lagerorte.get(ziel.id)?.aktiv === true;
}

/**
 * Wo ein Artikel war, in Worten: „in GB-03“, „im Einsatz“, „verliehen“,
 * „noch nicht erfasst“. Fuer den Hinweis nach einem Scan.
 */
export function woWar(bestand: Bestand, zustand: Zustand): string {
  switch (zustand.verbleib) {
    case "nicht_erfasst":
      return "noch nicht erfasst";
    case "ausgeliehen":
      return "verliehen";
    case "im_einsatz": {
      const stelle =
        zustand.einsatzstelle_id === null
          ? undefined
          : bestand.einsatzstellen.get(zustand.einsatzstelle_id);
      return stelle === undefined ? "im Einsatz" : `im Einsatz — ${stelle.bezeichnung}`;
    }
    case "im_lager": {
      const ort =
        zustand.lagerort_id === null ? undefined : bestand.lagerorte.get(zustand.lagerort_id);
      if (ort === undefined) return "an unbekanntem Ort";
      if (ort.standardziel || ort.unbekannt) return `in „${ort.bezeichnung}“`;
      const dazu = packstueckStandort(bestand, ort.id);
      return dazu === null ? `in ${ort.kennung}` : `in ${ort.kennung} (${dazu})`;
    }
  }
}

/** Liegt ein Lagerort in einem anderen, auch tiefer? Er selbst zaehlt mit. */
export function liegtIn(
  bestand: Bestand,
  lagerort_id: string | null,
  behaelter_id: string,
): boolean {
  let aktuell = lagerort_id === null ? undefined : bestand.lagerorte.get(lagerort_id);
  const gesehen = new Set<string>();
  while (aktuell !== undefined && !gesehen.has(aktuell.id)) {
    if (aktuell.id === behaelter_id) return true;
    gesehen.add(aktuell.id);
    aktuell = uebergeordnet(bestand, aktuell) ?? undefined;
  }
  return false;
}

/** Was nach einer Buchung fuer „Rueckgaengig“ gebraucht wird. */
export interface Gebucht {
  artikel: Artikel;
  /** Der Zustand vor der Buchung -- dahin fuehrt „Rueckgaengig“. */
  vorher: Zustand;
}

function zustandVon(bestand: Bestand, artikel_id: string): Zustand {
  return bestand.zustaende.get(artikel_id) ?? zustandOhneBuchung(statusRegeln(bestand.statusArten));
}

/**
 * Eine Buchung aus einer Bewegung. `versatz` in Millisekunden haelt mehrere
 * Buchungen eines Vorgangs in ihrer Reihenfolge -- die Ableitung sortiert nach
 * `erfasst_am`.
 */
function buchungBauen(
  artikel_id: string,
  bewegung: Bewegung,
  versatz = 0,
  kommentar: string | null = null,
  vorgang_id: string | null = null,
): BuchungAnlegen {
  const sitzung = sitzungLesen();
  if (sitzung === null) throw new Error("Nicht angemeldet");
  return {
    id: neueBuchungsKennung(),
    artikel_id,
    art: bewegung.art,
    ziel_lagerort_id: bewegung.ziel_lagerort_id,
    einsatzstelle_id: bewegung.einsatzstelle_id,
    menge: null,
    menge_absolut: null,
    status: bewegung.status,
    benutzer_id: sitzung.benutzer.id,
    erfasst_am: new Date(Date.now() + versatz).toISOString(),
    geraet: geraeteKennung(),
    vorgang_id,
    abgeleitet_von: null,
    modul: null,
    kommentar,
    fotos: [],
  };
}

function artikelVon(bestand: Bestand, artikel_id: string): Artikel {
  const artikel = bestand.artikel.get(artikel_id);
  if (artikel === undefined) throw new Error("Diesen Artikel kennt das Gerät nicht");
  return artikel;
}

/**
 * Mehrere Buchungen, die zusammengehoeren: „Alles da“, eine Aktion im
 * Mehrfachscan. Sie tragen dieselbe `vorgang_id` -- so steht es im Log --, und
 * jede weitere liegt eine Millisekunde nach der vorigen, damit die Ableitung
 * sie in dieser Reihenfolge sieht.
 */
export interface Vorgang {
  id: string;
  /** Die wievielte Buchung des Vorgangs das ist, von 0 an. */
  stelle: number;
}

/** Ein neuer Vorgang. `stelle` zaehlt der Aufrufer hoch. */
export function neuerVorgang(): Vorgang {
  return { id: neueBuchungsKennung(), stelle: 0 };
}

/**
 * Einen Artikel an ein Ziel verschieben (ADR 0013 §1).
 *
 * `bestand` sollte frisch sein (`bestandLaden`): Beim Dauerscan kommt der
 * naechste Code, bevor die Anzeige nachgezogen hat, und der Zustand „vorher“
 * muss trotzdem stimmen.
 */
export async function verschieben(
  bestand: Bestand,
  artikel_id: string,
  ziel: Ziel,
  vorgang?: Vorgang,
): Promise<Gebucht> {
  const artikel = artikelVon(bestand, artikel_id);
  if (verboten(bestand, artikel_id, ziel)) {
    throw new Error(`${artikel.bezeichnung} kann nicht in sich selbst stecken`);
  }
  const vorher = zustandVon(bestand, artikel_id);
  const behaelter =
    ziel.art === "lagerort" ? (bestand.lagerorte.get(ziel.id)?.artikel_id ?? null) : null;
  const bewegung = verschiebenNach(
    vorher,
    ziel,
    behaelter === null ? null : (bestand.zustaende.get(behaelter) ?? null),
  );
  return bewegungBuchen(bestand, artikel_id, bewegung, vorgang);
}

/**
 * Eine fertig berechnete Bewegung buchen -- fuer „Alles da“, das die Bewegung
 * mit dem rechnet, was die Buchungen davor schon bewegt haben
 * (`allesDaBewegungen`).
 */
export async function bewegungBuchen(
  bestand: Bestand,
  artikel_id: string,
  bewegung: Bewegung,
  vorgang?: Vorgang,
): Promise<Gebucht> {
  const artikel = artikelVon(bestand, artikel_id);
  const vorher = zustandVon(bestand, artikel_id);
  await buchen(buchungBauen(artikel_id, bewegung, vorgang?.stelle ?? 0, null, vorgang?.id ?? null));
  return { artikel, vorher };
}

/** Den Status setzen (ADR 0005). Der Ort bleibt. */
export async function statusSetzen(
  bestand: Bestand,
  artikel_id: string,
  status: string,
  vorgang?: Vorgang,
): Promise<Gebucht> {
  return bewegungBuchen(
    bestand,
    artikel_id,
    { art: "statusaenderung", ziel_lagerort_id: null, einsatzstelle_id: null, status },
    vorgang,
  );
}

/**
 * „Rueckgaengig“: Gegenbuchungen auf den Zustand von vorher (ADR 0013 §15).
 * Liest den Bestand selbst -- die Buchung, die zurueckgenommen wird, kann
 * Augenblicke alt sein. Liefert die Zahl der Buchungen.
 *
 * Ein ganzer Vorgang -- nach „Alles da“ -- wird von hinten zurueckgenommen:
 * erst der Inhalt aus den Kisten, dann die Kisten. Was nur mit einer Kiste
 * mitkam, geht mit ihr zurueck.
 */
export async function zuruecknehmen(gebucht: Gebucht | readonly Gebucht[]): Promise<number> {
  const liste = "artikel" in gebucht ? [gebucht] : [...gebucht].reverse();
  const bestand = await bestandLaden();
  const ziel = standardziel(bestand)?.id ?? null;
  const vorgang_id = liste.length > 1 ? neueBuchungsKennung() : null;

  let stelle = 0;
  for (const g of liste) {
    const jetzt = zustandVon(bestand, g.artikel.id);
    for (const b of rueckgaengig(g.vorher, jetzt, ziel)) {
      await buchen(buchungBauen(g.artikel.id, b, stelle, RUECKGAENGIG_KOMMENTAR, vorgang_id));
      stelle += 1;
    }
  }
  return stelle;
}
